import { AxiosError, AxiosResponse, CanceledError } from 'axios'
import { api } from 'api'
import { IDelete, IFetch, IPatch, IPost, IPut, IRequest } from './types'

/**
 * Fetches data from a specified endpoint using Axios.
 *
 * @template T The expected return type of the API response.
 * @param {string} endpoint The API endpoint to fetch data from.
 * @param {object} [params] Optional parameters to pass with the request.
 *
 * @returns {Promise<T>} Returns a promise that resolves with the fetched data.
 *
 * @throws Will throw an error if the API request fails. The thrown error will contain details about the failure.
 */
export async function fetchData<T>({ endpoint, params = {}, ...options }: IFetch) {
  try {
    const response = await api.get<T>(endpoint, { params, ...options })
    return response.data
  } catch (error) {
    if (error instanceof CanceledError) {
      throw new Error('Request canceled', { cause: error })
    }
    if (!(error instanceof AxiosError)) {
      throw error
    }

    if (error.response) {
      throw error.response.data
    } else if (error.request) {
      throw new Error('An error occurred while fetching data.', { cause: error })
    } else {
      throw new Error(error.message, { cause: error })
    }
  }
}

/**
 * Fetches data from a specified endpoint using Axios.
 *
 * @template T The expected return type of the API response.
 * @param {IRequestMethod} method The HTTP method to use.
 * @param {string} endpoint The API endpoint to fetch data from.
 * @param {object} [params] Optional parameters to pass with the request.
 *
 * @returns {Promise<T>} Returns a promise that resolves with the fetched data.
 *
 * @throws Will throw an error if the API request fails. The thrown error will contain details about the failure.
 */
export async function request<T, Options>({
  method,
  endpoint,
  queryOptions,
  axiosOptions = {},
  errorCallback,
}: IRequest<Options>) {
  try {
    const response = await api.request<Options, AxiosResponse<T>>({
      method,
      url: endpoint,
      data: queryOptions,
      ...axiosOptions,
    })
    return response.data
  } catch (error: unknown) {
    if (error instanceof CanceledError) {
      throw new Error('Request canceled', { cause: error })
    }

    let caughtError: Error
    if (error instanceof Error) {
      caughtError = error
    } else {
      caughtError = new Error('An unknown error occurred', { cause: error })
    }

    // TODO: we need to map out what the global error catching/displaying pattern(s) are
    // then we need to refactor the codebase to consistently follow our desired behavior

    if (errorCallback) {
      errorCallback(caughtError)
      caughtError = new Error('', { cause: error }) // reset the error to avoid having index.jsx handle it
    }

    // TODO: // we need to throw to resolve this clause, or it causes unexpected behavior
    // in our implementations of react query. this should be totally overhauled
    throw caughtError
  }
}

/**
 * Fetches data from a specified endpoint using Axios.
 *
 * @template T The expected return type of the API response.
 * @param {string} endpoint The API endpoint to fetch data from.
 * @param {object} [params] Optional parameters to pass with the request.
 *
 * @returns {Promise<T>} Returns a promise that resolves with the fetched data.
 *
 * @throws Will throw an error if the API request fails. The thrown error will contain details about the failure.
 */
export const postData = async <T, Options>(options: IPost<Options>) =>
  request<T, Options>({ method: 'post', ...options })
export const putData = async <T, Options>(options: IPut<Options>) =>
  request<T, Options>({ method: 'put', ...options })
export const patchData = async <T, Options>(options: IPatch<Options>) =>
  request<T, Options>({ method: 'patch', ...options })
export const deleteData = async <T, Options = never>(options: IDelete<Options>) =>
  request<T, Options>({ method: 'delete', ...options })
